/**
 * Currency formatting utilities.
 *
 * Default currency is MYR (the platform's original market). Pass the tenant /
 * product currency to render the correct symbol for non-MYR stores. The MYR
 * output is preserved byte-for-byte ("RM 50.00") so existing stores are
 * unaffected; only a non-MYR currency switches to locale-aware formatting.
 */

const formatWithCurrency = (value: number, currency: string): string => {
  const code = (currency || 'MYR').toUpperCase();
  if (code === 'MYR') return `RM ${value.toFixed(2)}`;
  try {
    return new Intl.NumberFormat('en', { style: 'currency', currency: code }).format(value);
  } catch {
    return `${code} ${value.toFixed(2)}`;
  }
};

/**
 * Format price to a currency string.
 * @param amount - Amount in cents (e.g., 5000 = RM 50.00)
 * @param includeCurrency - Whether to include the currency symbol
 * @param currency - ISO currency code (defaults to MYR)
 */
export const formatPrice = (amount: number, includeCurrency = true, currency = 'MYR'): string => {
  const value = amount / 100;
  return includeCurrency ? formatWithCurrency(value, currency) : value.toFixed(2);
};

/**
 * Alias for formatPrice - Format currency
 */
export const formatCurrency = formatPrice;

/**
 * Format price from a major-unit amount (already in ringgit / dollars).
 * @param amount - Amount in major units (e.g., 50.00)
 * @param includeCurrency - Whether to include the currency symbol
 * @param currency - ISO currency code (defaults to MYR)
 */
export const formatPriceFromRinggit = (amount: number, includeCurrency = true, currency = 'MYR'): string => {
  return includeCurrency ? formatWithCurrency(amount, currency) : amount.toFixed(2);
};

/**
 * Calculate discount amount
 */
export const calculateDiscount = (
  originalPrice: number,
  discountType: 'percentage' | 'fixed',
  discountValue: number
): number => {
  if (discountType === 'percentage') {
    return originalPrice * (discountValue / 100);
  }
  return discountValue;
};

/**
 * Calculate final price after discount
 */
export const calculateFinalPrice = (
  originalPrice: number,
  discountType: 'percentage' | 'fixed',
  discountValue: number
): number => {
  const discount = calculateDiscount(originalPrice, discountType, discountValue);
  return Math.max(0, originalPrice - discount);
};

/**
 * Format large numbers with thousands separator
 */
export const formatNumber = (num: number): string => {
  return num.toLocaleString('en-MY');
};

/**
 * Calculate percentage savings
 */
export const calculateSavingsPercentage = (
  originalPrice: number,
  finalPrice: number
): number => {
  if (originalPrice <= 0) return 0;
  return Math.round(((originalPrice - finalPrice) / originalPrice) * 100);
};
